import {
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { SelectButton } from 'primeng/selectbutton';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';
import {
    AiAssistApiError,
    AiAssistService,
    AI_ASSIST_ICON,
    FinalizeCandidate,
    FinalizeResult,
    VENDOR_FLUX,
} from '@/app/services/ai-assist.service';
import { BrandKitImage } from '@/app/services/brand-kit.service';

type Phase = 'form' | 'loading' | 'results';

/**
 * "Generate with AI" dialog for the realm social-preview (OG) image. Overlays
 * the brand name + tagline onto the assigned Site Template's text-free OG base
 * plate (shown read-only) via `asset/finalize` (`asset_type='og'`), polls the
 * async job, and shows the candidate cards. Picking one yields the OG image URL
 * via `(applied)`.
 */
@Component({
    selector: 'app-ai-og-generate',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Dialog,
        InputText,
        SelectButton,
        Message,
        ProgressSpinner,
        Tooltip,
    ],
    templateUrl: './ai-og-generate.html',
})
export class AiOgGenerate {
    private readonly aiAssist = inject(AiAssistService);

    readonly realmId = input.required<string>();
    /** Brand name overlaid as the OG headline. */
    readonly title = input<string>('');
    /** Default tagline (e.g. the SEO description) — prefilled, editable. */
    readonly defaultTagline = input<string>('');
    /** Brand-kit OG base plate (read-only image) from the assigned Site Template. */
    readonly ogBasePlate = input<BrandKitImage | null>(null);

    /** Disable the trigger (service down / missing template elements) + the why. */
    readonly disabled = input<boolean>(false);
    readonly disabledReason = input<string | null>(null);

    readonly applied = output<string>();

    protected readonly aiIcon = AI_ASSIST_ICON;

    protected readonly visible = signal(false);
    protected readonly phase = signal<Phase>('form');
    protected readonly error = signal<string | null>(null);

    protected readonly tagline = signal('');
    protected readonly samples = signal(4);
    protected readonly sampleOptions = [
        { label: '1', value: 1 },
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
    ];

    protected readonly candidates = signal<FinalizeCandidate[]>([]);

    private abort: AbortController | null = null;

    protected readonly hasTitle = computed(
        () => this.title().trim().length > 0,
    );

    /**
     * Non-null when FLUX (used by finalize) is out of credits — a UX pre-check
     * mirroring the service's pre-flight gate, so the operator sees why the
     * Generate button is disabled before hitting a 400.
     */
    protected readonly creditBlock = computed<string | null>(() =>
        this.aiAssist.blockedVendors().includes(VENDOR_FLUX)
            ? 'Generation paused — FLUX is out of credits. Top up the vendor account to continue.'
            : null,
    );

    protected readonly canGenerate = computed<boolean>(() => {
        if (!this.hasTitle() || this.tagline().trim().length === 0) return false;
        if (this.creditBlock()) return false;
        return this.ogBasePlate() !== null;
    });

    protected open(): void {
        if (this.disabled()) return;
        this.reset();
        this.tagline.set(this.defaultTagline());
        void this.aiAssist.refreshVendorBalances();
        this.visible.set(true);
    }

    protected onVisibleChange(value: boolean): void {
        if (!value) this.cancelPolling();
        this.visible.set(value);
    }

    protected async generate(): Promise<void> {
        if (!this.canGenerate() || this.phase() === 'loading') return;
        this.cancelPolling();
        this.abort = new AbortController();
        this.phase.set('loading');
        this.error.set(null);
        this.candidates.set([]);
        try {
            const accepted = await this.aiAssist.finalizeImage({
                jobId: crypto.randomUUID(),
                realmId: this.realmId(),
                basePlateB64: this.ogBasePlate()?.b64 ?? null,
                assetType: 'og',
                title: this.title().trim(),
                tagline: this.tagline().trim(),
                samples: this.samples(),
            });
            const done = await this.aiAssist.awaitJob(accepted.job_id, {
                signal: this.abort.signal,
            });
            const result = done.result as FinalizeResult | null;
            this.candidates.set(result?.candidates ?? []);
            this.phase.set('results');
        } catch (err) {
            this.error.set(this.friendlyError(err));
            this.phase.set('form');
        }
    }

    protected use(candidate: FinalizeCandidate): void {
        this.applied.emit(candidate.url);
        this.visible.set(false);
    }

    protected backToForm(): void {
        this.phase.set('form');
    }

    private cancelPolling(): void {
        this.abort?.abort();
        this.abort = null;
    }

    private reset(): void {
        this.cancelPolling();
        this.phase.set('form');
        this.error.set(null);
        this.candidates.set([]);
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service. Please try again.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'Generation took too long. Please try again.';
            case 'vendor_unavailable':
                return 'The image model is temporarily unavailable. Please try again shortly.';
            case 'rate_limited':
                return 'Too many requests right now. Please wait a moment and retry.';
            case 'content_moderated':
                return 'The request was blocked by the content filter. Try a different tagline or base image.';
            case 'invalid_references':
                return 'The base image could not be used (must be a public, text-free image).';
            case 'unauthorized':
                return 'Your session is not authorized. Please sign in again.';
            case 'forbidden_realm':
                return 'You do not have access to this realm.';
            case 'invalid_request':
                return err.message ?? 'The request was rejected as invalid.';
            default:
                return err.message ?? 'Something went wrong. Please try again.';
        }
    }
}
